(() => {
  if (window.__nnMobileSplitBuilderLoaded) return;
  window.__nnMobileSplitBuilderLoaded = true;

  const mobile = window.matchMedia('(max-width: 749px)');
  let state;

  const holdMobileHomepageAtHero = () => {
    const homepageLoad = location.pathname === '/' && !location.hash && mobile.matches;
    if (!homepageLoad) return;

    try {
      const currentState = typeof history.state === 'object' && history.state ? history.state : {};
      history.replaceState({ ...currentState, scrollTop: 0 }, '');
    } catch {
      // The top-of-page correction still works if history state is unavailable.
    }

    const previousRestoration = history.scrollRestoration;
    const interactions = new AbortController();
    let released = false;
    let finalTimer;
    const keepHeroVisible = () => {
      if (released) return;
      const focused = document.activeElement;
      if (focused && focused !== document.body && focused !== document.documentElement) focused.blur?.();
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    };
    const release = () => {
      if (released) return;
      released = true;
      window.clearTimeout(finalTimer);
      interactions.abort();
      history.scrollRestoration = previousRestoration;
      document.documentElement.classList.remove('nn-home-load-lock');
    };

    history.scrollRestoration = 'manual';
    document.documentElement.classList.add('nn-home-load-lock');
    ['touchstart', 'pointerdown', 'wheel', 'keydown'].forEach((eventName) => {
      window.addEventListener(eventName, release, {
        once: true,
        passive: true,
        signal: interactions.signal
      });
    });
    keepHeroVisible();
    window.addEventListener('pageshow', () => window.requestAnimationFrame(keepHeroVisible), { once: true });
    window.addEventListener('load', () => {
      keepHeroVisible();
      window.setTimeout(keepHeroVisible, 250);
      window.setTimeout(keepHeroVisible, 800);
    }, { once: true });
    finalTimer = window.setTimeout(() => {
      keepHeroVisible();
      release();
    }, 1800);
  };

  holdMobileHomepageAtHero();

  const createDirectRoute = () => {
    const route = document.createElement('header');
    route.className = 'nn-builder-route nn-builder-route--direct';
    route.id = 'nn-shop-routes';
    route.innerHTML = `
      <p>THE COMPASS COLLECTION™</p>
      <h2>Find Your Compass</h2>
      <span>Four expressions of the same story. Begin with the Compass that feels unmistakably yours.</span>
      <div class="nn-builder-route__path-choice" aria-label="Choose how to begin">
        <a href="#nn-map-of-memories">Personalise with Charms <span aria-hidden="true">→</span></a>
        <i aria-hidden="true">◇</i>
        <a href="#nn-compass-cards">Discover the Bags <span aria-hidden="true">↓</span></a>
      </div>
    `;
    return route;
  };

  const createMemory = () => {
    const memory = document.createElement('div');
    memory.className = 'nn-map-builder__journey-memory';
    memory.setAttribute('aria-live', 'polite');
    memory.innerHTML = '<span>Your journey so far</span><strong></strong>';
    return memory;
  };

  const createCartographicBridge = () => {
    const bridge = document.createElement('figure');
    bridge.className = 'nn-cartographic-bridge';
    bridge.setAttribute('aria-label', 'A gold world map marked with places visited');

    const script = document.querySelector('script[src*="nn-mobile-split-builder.js"]');
    const mapUrl = script?.src ? new URL('nn-world-map-metallic.webp', script.src).href : '';
    bridge.innerHTML = `
      ${mapUrl ? `<img src="${mapUrl}" alt="" width="900" height="417" loading="lazy" decoding="async">` : ''}
      <svg viewBox="0 0 1000 460" role="img" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="nn-map-pin-metal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#ead8aa"></stop>
            <stop offset=".48" stop-color="#c6a769"></stop>
            <stop offset="1" stop-color="#8e7350"></stop>
          </linearGradient>
          <filter id="nn-map-pin-shadow" x="-60%" y="-50%" width="220%" height="230%">
            <feDropShadow dx="0" dy="2" stdDeviation="1.8" flood-color="#5d492e" flood-opacity=".34"></feDropShadow>
          </filter>
        </defs>
        <g class="nn-cartographic-bridge__pins" filter="url(#nn-map-pin-shadow)">
          <g transform="translate(255 170)"><path d="M0 17C-2.2 12.4-10 4.2-10-3.2A10 10 0 1 1 10-3.2C10 4.2 2.2 12.4 0 17Z"></path><circle cy="-3.2" r="3.4"></circle></g>
          <g transform="translate(475 152)"><path d="M0 17C-2.2 12.4-10 4.2-10-3.2A10 10 0 1 1 10-3.2C10 4.2 2.2 12.4 0 17Z"></path><circle cy="-3.2" r="3.4"></circle></g>
          <g transform="translate(449 236)"><path d="M0 17C-2.2 12.4-10 4.2-10-3.2A10 10 0 1 1 10-3.2C10 4.2 2.2 12.4 0 17Z"></path><circle cy="-3.2" r="3.4"></circle></g>
          <g transform="translate(338 305)"><path d="M0 17C-2.2 12.4-10 4.2-10-3.2A10 10 0 1 1 10-3.2C10 4.2 2.2 12.4 0 17Z"></path><circle cy="-3.2" r="3.4"></circle></g>
          <g transform="translate(687 247)"><path d="M0 17C-2.2 12.4-10 4.2-10-3.2A10 10 0 1 1 10-3.2C10 4.2 2.2 12.4 0 17Z"></path><circle cy="-3.2" r="3.4"></circle></g>
          <g transform="translate(848 336)"><path d="M0 17C-2.2 12.4-10 4.2-10-3.2A10 10 0 1 1 10-3.2C10 4.2 2.2 12.4 0 17Z"></path><circle cy="-3.2" r="3.4"></circle></g>
        </g>
      </svg>
      <figcaption>Places visited, marked on a personal map of memories.</figcaption>
    `;
    return bridge;
  };

  const createPanel = (step, chapter, label) => {
    const panel = document.createElement('section');
    panel.className = `nn-map-builder nn-map-builder--split-panel nn-map-builder--split-${chapter}`;
    panel.dataset.nnSplitBuilderPanel = chapter;
    panel.setAttribute('aria-label', label);
    panel.innerHTML =
      '<div class="nn-map-builder__inner page-width">' +
        '<div class="nn-map-builder__copy">' +
          '<div class="nn-map-builder__steps"></div>' +
        '</div>' +
      '</div>';
    panel.querySelector('.nn-map-builder__steps').appendChild(step);
    return panel;
  };

  const mount = () => {
    if (location.pathname !== '/' || !mobile.matches || state) return;

    const root = document.querySelector('[data-nn-map-builder]');
    const stepsWrap = root?.querySelector('.nn-map-builder__steps');
    const steps = stepsWrap ? [...stepsWrap.children].filter((child) => child.classList.contains('nn-map-builder__step')) : [];
    const charmCards = document.querySelector('[id$="__product_list_fjjQTj"]');
    const compassCards = document.querySelector('[id$="__product_list_fa6P9H"]');
    const chapterThree = document.querySelector('.nn-editorial-collage--chapter-three');
    const copy = root?.querySelector('.nn-map-builder__copy');
    const inner = root?.querySelector('.nn-map-builder__inner');
    if (!root || steps.length !== 3 || !charmCards || !compassCards || !chapterThree || !copy || !inner) return;

    const compassSessionKey = 'nn-current-compass';
    const builderSelect = root.querySelector('[data-nn-bag-select]');
    const availableCompassHandles = new Set(
      [...(builderSelect?.options || [])].map((option) => option.value).filter(Boolean)
    );
    const readRememberedCompass = () => {
      try {
        const handle = window.sessionStorage.getItem(compassSessionKey);
        return availableCompassHandles.has(handle) ? handle : '';
      } catch {
        return '';
      }
    };
    const rememberCompass = (handle) => {
      if (!builderSelect || !availableCompassHandles.has(handle)) return;
      root.dataset.nnViewedCompass = handle;
      try {
        window.sessionStorage.setItem(compassSessionKey, handle);
      } catch {
        // The builder still updates when storage is unavailable.
      }
      if (builderSelect.value !== handle) {
        builderSelect.value = handle;
        builderSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
    const rememberedCompass = readRememberedCompass();
    root.dataset.nnCompassHandoff = 'ready';
    if (rememberedCompass) rememberCompass(rememberedCompass);

    const compassProductCards = [...compassCards.querySelectorAll('product-card')]
      .map((card) => {
        const href = card.querySelector('.product-card__link[href]')?.getAttribute('href');
        const handle = href?.match(/\/products\/([^/?#]+)/)?.[1] || '';
        return availableCompassHandles.has(handle) ? { card, handle } : null;
      })
      .filter(Boolean);
    const compassAttention = { frame: 0, timer: 0, candidate: '' };
    const visibleCard = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const candidates = compassProductCards.map(({ card, handle }) => {
        const rect = card.getBoundingClientRect();
        const horizontal = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
        const vertical = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
        const horizontalRatio = horizontal / Math.max(1, Math.min(rect.width, viewportWidth));
        const verticalPresence = vertical / Math.max(1, viewportHeight);
        return { handle, horizontalRatio, verticalPresence };
      }).filter(({ horizontalRatio, verticalPresence }) => horizontalRatio >= 0.62 && verticalPresence >= 0.18);
      return candidates.sort((a, b) =>
        (b.horizontalRatio + b.verticalPresence) - (a.horizontalRatio + a.verticalPresence)
      )[0]?.handle || '';
    };
    const measureCompassAttention = () => {
      compassAttention.frame = 0;
      const candidate = visibleCard();
      if (!candidate || candidate === compassAttention.candidate) return;
      compassAttention.candidate = candidate;
      window.clearTimeout(compassAttention.timer);
      compassAttention.timer = window.setTimeout(() => {
        if (visibleCard() === candidate) rememberCompass(candidate);
      }, 480);
    };
    const scheduleCompassAttention = () => {
      if (!compassAttention.frame) {
        compassAttention.frame = window.requestAnimationFrame(measureCompassAttention);
      }
    };
    const manualCompassChange = () => rememberCompass(builderSelect?.value);
    const compassCardInteraction = (event) => {
      if (event.target.closest('button')) return;
      const card = event.target.closest('product-card');
      const match = compassProductCards.find((item) => item.card === card);
      if (match) rememberCompass(match.handle);
    };
    window.addEventListener('scroll', scheduleCompassAttention, { passive: true });
    window.addEventListener('resize', scheduleCompassAttention, { passive: true });
    compassCards.addEventListener('scroll', scheduleCompassAttention, { passive: true, capture: true });
    compassCards.addEventListener('click', compassCardInteraction, true);
    builderSelect?.addEventListener('change', manualCompassChange);
    const compassCardObserver = new IntersectionObserver(scheduleCompassAttention, {
      threshold: [0, 0.25, 0.5, 0.75]
    });
    compassProductCards.forEach(({ card }) => compassCardObserver.observe(card));
    const compassAttentionInterval = window.setInterval(scheduleCompassAttention, 650);
    scheduleCompassAttention();

    const secondMarker = document.createComment('North & Noble builder step 02 position');
    const thirdMarker = document.createComment('North & Noble builder step 03 position');
    steps[1].before(secondMarker);
    steps[2].before(thirdMarker);

    const panelTwo = createPanel(steps[1], '02', 'Chapter 02 — Choose the places in your story');
    const panelThree = createPanel(steps[2], '03', 'Chapter 03 — Review and add your story to the bag');
    const directRoute = createDirectRoute();
    const compassCardsAnchor = document.createElement('span');
    compassCardsAnchor.id = 'nn-compass-cards';
    compassCardsAnchor.className = 'nn-builder-route__cards-anchor';
    compassCardsAnchor.setAttribute('aria-hidden', 'true');
    const guidedRoute = document.createElement('header');
    guidedRoute.className = 'nn-builder-route nn-builder-route--guided page-width';
    const cartographicBridge = createCartographicBridge();
    const guidedNodes = [
      copy.querySelector('.nn-map-builder__eyebrow'),
      copy.querySelector('h2'),
      copy.querySelector('.nn-map-builder__intro'),
      copy.querySelector('.nn-map-builder__progress')
    ].filter(Boolean);
    guidedNodes.forEach((node) => guidedRoute.appendChild(node));
    guidedRoute.querySelector('h2')?.after(cartographicBridge);
    const guidedIntro = guidedRoute.querySelector('.nn-map-builder__intro');
    const guidedIntroOriginalHTML = guidedIntro?.innerHTML || '';
    if (guidedIntro) {
      guidedIntro.textContent = 'Your Compass is only the beginning. Collect the places that matter and carry your story.';
    }

    const memory = createMemory();
    panelTwo.querySelector('.nn-map-builder__copy').prepend(memory);
    const summaryBag = panelThree.querySelector('[data-nn-summary-bag]');
    const summaryCharms = panelThree.querySelector('[data-nn-summary-charms]');
    const checkoutContent = panelThree.querySelector('.nn-map-builder__step-content');
    const checkoutLabel = checkoutContent?.querySelector('.nn-map-builder__label');
    const checkoutOriginalNodes = checkoutContent ? [...checkoutContent.childNodes] : [];
    const checkoutLabelText = checkoutLabel?.textContent || '';
    const checkoutSummary = checkoutContent?.querySelector('.nn-map-builder__summary');
    const checkoutButton = checkoutContent?.querySelector('[data-nn-submit]');
    const edition = checkoutContent?.querySelector('.nn-map-builder__edition');
    const editionOriginalHTML = edition?.innerHTML || '';
    const storyBagData = (() => {
      try {
        const data = JSON.parse(root.querySelector('[data-nn-builder-data]')?.textContent || '{}');
        return new Map((data.bags || []).map((bag) => [bag.handle, bag]));
      } catch {
        return new Map();
      }
    })();
    const storyPortrait = document.createElement('figure');
    storyPortrait.className = 'nn-map-builder__story-portrait';
    storyPortrait.innerHTML = `
      <div class="nn-map-builder__story-product">
        <img src="" alt="" width="700" height="700" loading="lazy" decoding="async">
      </div>
      <figcaption>
        <span>Your Compass</span>
        <strong></strong>
        <em></em>
        <div class="nn-map-builder__story-places">
          <span>Your Places</span>
          <p aria-live="polite">None chosen yet</p>
        </div>
      </figcaption>
    `;
    const storyPortraitImage = storyPortrait.querySelector('img');
    const storyPortraitName = storyPortrait.querySelector('strong');
    const storyPortraitPrice = storyPortrait.querySelector('em');
    const storyPortraitPlaces = storyPortrait.querySelector('.nn-map-builder__story-places p');
    const storyPortraitObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35)) return;
      storyPortrait.classList.add('is-visible');
      storyPortraitObserver.disconnect();
    }, { threshold: [0.35] });
    storyPortraitObserver.observe(storyPortrait);
    const completion = document.createElement('p');
    completion.className = 'nn-map-builder__completion';
    const giftStory = document.createElement('aside');
    giftStory.className = 'nn-map-builder__gift-story';
    giftStory.innerHTML = `
      <span aria-hidden="true">◇</span>
      <div>
        <strong>A gift that becomes a lifetime of memories</strong>
        <p>Made to hold the places, people and moments they never want to forget—and the journeys still waiting to be lived.</p>
      </div>
    `;
    const multiple = document.createElement('div');
    multiple.className = 'nn-map-builder__multiple';
    multiple.innerHTML = `
      <button type="button" class="nn-map-builder__multiple-toggle" aria-expanded="false">
        <span>Creating more than one story?</span><span aria-hidden="true">+</span>
      </button>
      <div class="nn-map-builder__multiple-panel" hidden>
        <div class="nn-map-builder__quantity-row">
          <span><strong>Compass quantity</strong><small>Duplicate this chosen Compass</small></span>
          <span class="nn-map-builder__quantity" data-nn-quantity>
            <button type="button" aria-label="Decrease Compass quantity" data-nn-quantity-minus>−</button>
            <input type="number" value="1" min="1" max="6" readonly aria-label="Compass quantity" data-nn-bag-quantity>
            <button type="button" aria-label="Increase Compass quantity" data-nn-quantity-plus>+</button>
          </span>
        </div>
        <label class="nn-map-builder__match-quantities">
          <input type="checkbox" checked data-nn-match-quantities>
          <span>Match each selected charm to the Compass quantity</span>
        </label>
        <div class="nn-map-builder__charm-quantities" data-nn-charm-quantities hidden></div>
      </div>
    `;
    const purchaseTrust = document.createElement('p');
    purchaseTrust.className = 'nn-map-builder__purchase-trust';
    purchaseTrust.textContent = 'Royal Mail 24h Tracked Delivery · 90-day returns · 12-month warranty';
    const anotherStory = document.createElement('button');
    anotherStory.type = 'button';
    anotherStory.className = 'nn-map-builder__another-story';
    anotherStory.dataset.nnCreateAnother = '';
    anotherStory.hidden = true;
    anotherStory.textContent = 'Create Another Story';
    anotherStory.addEventListener('click', () => window.location.reload());

    if (checkoutLabel) {
      checkoutLabel.textContent = 'Your Story, Ready to Carry';
      checkoutLabel.after(storyPortrait);
    }
    if (edition) {
      const icon = edition.querySelector('.nn-map-builder__edition-icon')?.outerHTML || '';
      const copy = edition.querySelector('em');
      edition.innerHTML = `
        <button type="button" class="nn-map-builder__edition-toggle" aria-expanded="false">
          ${icon}<strong>First Edition Collection</strong><span aria-hidden="true">+</span>
        </button>
        <div class="nn-map-builder__edition-copy" hidden>${copy?.outerHTML || ''}</div>
      `;
      edition.querySelector('.nn-map-builder__edition-toggle')?.addEventListener('click', (event) => {
        const button = event.currentTarget;
        const panel = edition.querySelector('.nn-map-builder__edition-copy');
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        button.querySelector('[aria-hidden="true"]:last-child').textContent = open ? '−' : '+';
        if (panel) panel.hidden = !open;
      });
    }
    if (checkoutSummary && checkoutButton) {
      checkoutSummary.after(multiple);
      multiple.after(giftStory);
      giftStory.after(checkoutButton);
      checkoutButton.after(purchaseTrust);
      purchaseTrust.after(anotherStory);
    }

    const quantityChange = () => {
      root.dataset.quantityTick = String(Date.now());
    };
    const clampQuantity = (value) => Math.max(1, Math.min(6, Number(value) || 1));
    const adjustQuantity = (control, direction) => {
      const input = control?.querySelector('input[type="number"]');
      if (!input) return;
      input.value = String(clampQuantity(Number(input.value) + direction));
    };
    multiple.querySelector('.nn-map-builder__multiple-toggle')?.addEventListener('click', (event) => {
      const button = event.currentTarget;
      const panel = multiple.querySelector('.nn-map-builder__multiple-panel');
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      button.querySelector('[aria-hidden="true"]').textContent = open ? '×' : '+';
      panel.hidden = !open;
    });
    multiple.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (button?.matches('[data-nn-quantity-minus]')) adjustQuantity(button.closest('[data-nn-quantity]'), -1);
      if (button?.matches('[data-nn-quantity-plus]')) adjustQuantity(button.closest('[data-nn-quantity]'), 1);
    });
    const matchQuantities = multiple.querySelector('[data-nn-match-quantities]');
    const charmQuantities = multiple.querySelector('[data-nn-charm-quantities]');
    matchQuantities?.addEventListener('change', () => {
      if (!matchQuantities.checked) syncCharmQuantities();
      charmQuantities.hidden = matchQuantities.checked;
      quantityChange();
    });
    const selectedCharms = document.querySelector('[data-nn-selected-charms]');
    const syncCharmQuantities = () => {
      if (!charmQuantities) return;
      const previous = new Map([...charmQuantities.querySelectorAll('[data-nn-charm-quantity]')].map((input) => [input.dataset.variantId, input.value]));
      const checked = [...document.querySelectorAll('[data-nn-charm-options] input:checked')];
      charmQuantities.innerHTML = checked.length ? '' : '<p>Select places in Chapter 02 to adjust their quantities.</p>';
      checked.forEach((input) => {
        const row = document.createElement('div');
        row.className = 'nn-map-builder__quantity-row';
        row.innerHTML = `
          <span><strong>${input.dataset.title}</strong><small>Country charm quantity</small></span>
          <span class="nn-map-builder__quantity" data-nn-quantity>
            <button type="button" aria-label="Decrease ${input.dataset.title} quantity" data-nn-quantity-minus>−</button>
            <input type="number" value="${previous.get(input.value) || 1}" min="1" max="6" readonly aria-label="${input.dataset.title} quantity" data-nn-charm-quantity data-variant-id="${input.value}">
            <button type="button" aria-label="Increase ${input.dataset.title} quantity" data-nn-quantity-plus>+</button>
          </span>
        `;
        charmQuantities.appendChild(row);
      });
      quantityChange();
    };
    const selectedObserver = new MutationObserver(syncCharmQuantities);
    if (selectedCharms) selectedObserver.observe(selectedCharms, { childList: true });
    document.addEventListener('change', (event) => {
      if (!event.target.matches('[data-nn-charm-options] input[type="checkbox"]')) return;
      window.setTimeout(() => {
        syncCharmQuantities();
        updateMemory();
      }, 0);
    });
    syncCharmQuantities();

    const updateMemory = () => {
      const rawBag = (summaryBag?.textContent || '').split('·')[0].trim() || 'Your Compass';
      const bag = rawBag.replace(/^\d+\s×\s/, '');
      const selectedBag = storyBagData.get(builderSelect?.value);
      if (selectedBag && storyPortraitImage && storyPortraitName && storyPortraitPrice) {
        if (storyPortraitImage.src !== selectedBag.image) storyPortraitImage.src = selectedBag.image;
        storyPortraitImage.alt = `${selectedBag.title} Compass bag, front view`;
        storyPortraitName.textContent = selectedBag.title;
        storyPortraitPrice.textContent = new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP'
        }).format((selectedBag.price || 0) / 100);
      }
      if (storyPortraitPlaces) {
        const placeNames = [...document.querySelectorAll('[data-nn-charm-options] input:checked')]
          .map((input) => input.dataset.title?.trim())
          .filter(Boolean);
        storyPortraitPlaces.textContent = placeNames.length ? placeNames.join(' · ') : 'None chosen yet';
      }
      const bagQuantity = Number(document.querySelector('[data-nn-bag-quantity]')?.value) || 1;
      const charmText = (summaryCharms?.textContent || '').trim();
      const charmCount = Number.parseInt(charmText, 10) || 0;
      const places = charmText === 'None selected'
        ? 'No places selected yet'
        : charmText.split('·')[0].trim().replace(' selected', charmText.startsWith('1 ') ? ' place selected' : ' places selected');
      memory.querySelector('strong').textContent = `${bag} selected · ${places}`;
      const subject = bagQuantity > 1 ? `${bagQuantity} ${bag} Compasses` : bag;
      completion.textContent = charmCount
        ? `${subject} with ${charmCount} ${charmCount === 1 ? 'place' : 'places'} chosen—made to carry the memories that matter to you.`
        : `${subject}, chosen as the beginning of the journeys still ahead.`;
    };
    const memoryObserver = new MutationObserver(updateMemory);
    if (summaryBag) memoryObserver.observe(summaryBag, { childList: true, characterData: true, subtree: true });
    if (summaryCharms) memoryObserver.observe(summaryCharms, { childList: true, characterData: true, subtree: true });

    compassCards.prepend(directRoute);
    directRoute.after(compassCardsAnchor);
    inner.after(guidedRoute);
    const cartographicBridgeObserver = new IntersectionObserver((entries) => {
      const entry = entries.find((candidate) => candidate.target === cartographicBridge);
      const rect = cartographicBridge.getBoundingClientRect();
      const genuinelyInView = entry?.isIntersecting &&
        entry.intersectionRatio >= 0.25 &&
        rect.top <= window.innerHeight * 0.84 &&
        rect.bottom >= 96;
      if (!genuinelyInView) return;
      cartographicBridge.classList.add('is-visible');
      cartographicBridgeObserver.disconnect();
    }, { threshold: [0.25] });
    const cartographicBridgeObserverTimer = window.setTimeout(
      () => cartographicBridgeObserver.observe(cartographicBridge),
      700
    );
    charmCards.after(panelTwo);
    chapterThree.after(panelThree);
    root.classList.add('nn-map-builder--split-first');
    updateMemory();

    state = {
      root, stepsWrap, steps, copy, secondMarker, thirdMarker, panelTwo, panelThree,
      directRoute, compassCardsAnchor, guidedRoute, guidedNodes, guidedIntro, guidedIntroOriginalHTML,
      memoryObserver, checkoutContent,
      cartographicBridge,
      checkoutOriginalNodes, checkoutLabel, checkoutLabelText, completion, giftStory,
      multiple, purchaseTrust, anotherStory, selectedObserver, edition, editionOriginalHTML,
      storyPortrait, storyPortraitObserver,
      scheduleCompassAttention, manualCompassChange, compassCardInteraction, builderSelect, compassCards,
      compassAttention, compassCardObserver, compassAttentionInterval,
      cartographicBridgeObserver, cartographicBridgeObserverTimer
    };
  };

  const unmount = () => {
    if (!state) return;
    const {
      root, steps, copy, secondMarker, thirdMarker, panelTwo, panelThree,
      directRoute, compassCardsAnchor, guidedRoute, guidedNodes, guidedIntro, guidedIntroOriginalHTML,
      memoryObserver, checkoutContent,
      cartographicBridge,
      checkoutOriginalNodes, checkoutLabel, checkoutLabelText, completion, giftStory,
      multiple, purchaseTrust, anotherStory, selectedObserver, edition, editionOriginalHTML,
      storyPortrait, storyPortraitObserver,
      scheduleCompassAttention, manualCompassChange, compassCardInteraction, builderSelect, compassCards,
      compassAttention, compassCardObserver, compassAttentionInterval,
      cartographicBridgeObserver, cartographicBridgeObserverTimer
    } = state;
    if (secondMarker.isConnected) secondMarker.after(steps[1]);
    if (thirdMarker.isConnected) thirdMarker.after(steps[2]);
    if (guidedIntro) guidedIntro.innerHTML = guidedIntroOriginalHTML;
    guidedNodes.forEach((node) => copy.insertBefore(node, copy.querySelector('.nn-map-builder__steps')));
    completion.remove();
    giftStory.remove();
    storyPortraitObserver.disconnect();
    storyPortrait.remove();
    multiple.remove();
    purchaseTrust.remove();
    anotherStory.remove();
    if (edition) edition.innerHTML = editionOriginalHTML;
    if (checkoutLabel) checkoutLabel.textContent = checkoutLabelText;
    checkoutOriginalNodes.forEach((node) => checkoutContent?.appendChild(node));
    memoryObserver.disconnect();
    selectedObserver.disconnect();
    window.cancelAnimationFrame(compassAttention.frame);
    window.clearTimeout(compassAttention.timer);
    window.clearInterval(compassAttentionInterval);
    compassCardObserver.disconnect();
    window.clearTimeout(cartographicBridgeObserverTimer);
    cartographicBridgeObserver.disconnect();
    window.removeEventListener('scroll', scheduleCompassAttention);
    window.removeEventListener('resize', scheduleCompassAttention);
    compassCards.removeEventListener('scroll', scheduleCompassAttention, true);
    compassCards.removeEventListener('click', compassCardInteraction, true);
    builderSelect?.removeEventListener('change', manualCompassChange);
    secondMarker.remove();
    thirdMarker.remove();
    directRoute.remove();
    compassCardsAnchor.remove();
    guidedRoute.remove();
    cartographicBridge.remove();
    panelTwo.remove();
    panelThree.remove();
    root.classList.remove('nn-map-builder--split-first');
    state = undefined;
  };

  const sync = () => mobile.matches ? mount() : unmount();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync, { once: true });
  } else {
    sync();
  }

  mobile.addEventListener('change', sync);
  document.addEventListener('shopify:section:load', () => {
    unmount();
    sync();
  });
})();
